import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Animated from 'react-native-reanimated';
import { getSyncingStatus, setRetainedConnections } from 'actions';
import { AppView, Button, withAnimatedNavigationHeader } from 'design-system';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import useRequiredTierCallback from 'features/premium/hooks/useRequiredTierCallback';
import { selectConnectionsLimit } from 'features/premium/selectors';
import { muteErrorAndResult, useFetchOld } from 'hooks/useFetch';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { selectActiveConnectionsWithoutManual, selectConnectionsWithoutManual } from 'reducers/selectors';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { strcmp } from 'utils/sort';
import { selectUserTier } from 'features/premium/selectors/tier';
import FlatListItemCard from 'design-system/FlatListItemCard';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { NavigationHeaderRightTextButton } from 'design-system/NavigationHeaderRightTextButton';
import { formatPlural, isUnlocked } from 'utils';
import { useDisableAndroidBackButton } from 'hooks';
import { SET_SELECTED_CONNECTIONS_BEFORE_UPGRADED_IN_LOGINS_UPGRADE_FUNNEL } from 'actions/types';
import { CommonActions } from '@react-navigation/native';
import { tierThemes } from 'features/premium/benefits';
import { getRequiredTier } from './utils';
import BankConnectionRow from './components/BankConnectionRow';
const strings = {
    title: 'Let’s activate your connections',
    button: 'Activate',
};
const keyExtractor = (item) => item.id.toString();
const ActivateConnectionsScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const selectedConnectionsBeforeUpgradedInLoginsUpgradeFunnel = useAppSelector((store) => store.user.selectedConnectionsBeforeUpgradedInLoginsUpgradeFunnel);
    const upgradedInLoginsUpgradeFunnel = !!selectedConnectionsBeforeUpgradedInLoginsUpgradeFunnel;
    const deactivatedConnectionsSnapshot = route.params.deactivatedConnections;
    useEffect(() => () => {
        dispatch({ type: SET_SELECTED_CONNECTIONS_BEFORE_UPGRADED_IN_LOGINS_UPGRADE_FUNNEL, payload: undefined });
    }, []);
    const allConnections = useRef(selectConnectionsWithoutManual(store.getState()));
    const activeConnections = useRef(selectActiveConnectionsWithoutManual(store.getState()));
    const connectionsAutomaticallyActivated = allConnections.current.length - deactivatedConnectionsSnapshot.length !== activeConnections.current.length;
    const connectionsLimitMap = useAppSelector(selectConnectionsLimit);
    const userTier = useAppSelector(selectUserTier);
    const orderedConnections = useMemo(() => 
    // If the user has upgraded in the LoginsUpgrade funnel, we show all connections as they have not been deactivated
    // and we don't know which connections the user would like to keep activated
    [...(upgradedInLoginsUpgradeFunnel ? allConnections.current : deactivatedConnectionsSnapshot)].sort((a, b) => {
        if (a.bankInfo.name !== b.bankInfo.name) {
            return strcmp(a.bankInfo.name, b.bankInfo.name);
        }
        return 0;
    }), [allConnections, deactivatedConnectionsSnapshot, upgradedInLoginsUpgradeFunnel]);
    const [selected, setSelected] = useState(selectedConnectionsBeforeUpgradedInLoginsUpgradeFunnel ?? {});
    const remainingLimit = useMemo(() => {
        const limit = connectionsLimitMap[userTier];
        if (limit === null)
            return null;
        if (upgradedInLoginsUpgradeFunnel) {
            return limit;
        }
        const remaining = limit -
            (connectionsAutomaticallyActivated
                ? activeConnections.current.length - orderedConnections.length
                : activeConnections.current.length);
        return remaining;
    }, [
        connectionsAutomaticallyActivated,
        connectionsLimitMap,
        orderedConnections.length,
        upgradedInLoginsUpgradeFunnel,
        userTier,
    ]);
    const allowSelection = useMemo(() => {
        if (remainingLimit === null || connectionsAutomaticallyActivated)
            return false;
        return remainingLimit < orderedConnections.length;
    }, [connectionsAutomaticallyActivated, orderedConnections.length, remainingLimit]);
    const close = useCallback(() => {
        const routes = navigation
            .getState()
            .routes.filter((r) => r.name !== 'ActivateConnections' && r.name !== 'LoginsUpgrade' && r.name !== 'LoginsUpgradeIntro');
        navigation.dispatch((state) => CommonActions.reset({ ...state, routes, index: routes.length - 1 }));
    }, []);
    useLayoutEffect(() => {
        if (!orderedConnections.length) {
            close();
            return;
        }
        navigation.setOptions({
            gestureEnabled: false,
            headerBackVisible: false,
            headerRight: connectionsAutomaticallyActivated || upgradedInLoginsUpgradeFunnel
                ? undefined
                : () => <NavigationHeaderRightTextButton text="Skip" onPress={close}/>,
        });
    }, [close, connectionsAutomaticallyActivated, orderedConnections.length, upgradedInLoginsUpgradeFunnel]);
    useDisableAndroidBackButton();
    const styles = useStyles(styleSet);
    const requiredTier = useMemo(() => {
        let totalSelected = orderedConnections.length;
        if (allowSelection) {
            totalSelected = Object.values(selected).filter(Boolean).length;
        }
        if (upgradedInLoginsUpgradeFunnel) {
            return getRequiredTier(connectionsLimitMap, totalSelected);
        }
        return getRequiredTier(connectionsLimitMap, activeConnections.current.length -
            (connectionsAutomaticallyActivated ? orderedConnections.length : 0) +
            totalSelected);
    }, [
        allowSelection,
        connectionsAutomaticallyActivated,
        connectionsLimitMap,
        orderedConnections.length,
        selected,
        upgradedInLoginsUpgradeFunnel,
    ]);
    const [, , isFetching, fetch] = useFetchOld(muteErrorAndResult);
    const onActivate = useRequiredTierCallback(useCallback(async () => {
        const userTierFromStore = selectUserTier(store.getState());
        if (!isUnlocked(userTierFromStore, requiredTier))
            return;
        const onSuccess = () => {
            navigation.navigate('Success', {
                subTitle: 'You’ve activated your bank connections!',
                onContinue: () => {
                    navigation.pop();
                    close();
                    return true;
                },
            });
        };
        const previousLimit = connectionsLimitMap[userTier];
        const currentLimit = connectionsLimitMap[userTierFromStore];
        if (connectionsAutomaticallyActivated || (previousLimit && !currentLimit)) {
            onSuccess();
            return;
        }
        const activeIds = activeConnections.current.map((connection) => connection.id);
        let selectedIds = orderedConnections.map((connection) => connection.id);
        if (allowSelection) {
            selectedIds = Object.keys(selected)
                .map(Number)
                .filter((key) => selected[key]);
        }
        const idsToActivate = upgradedInLoginsUpgradeFunnel ? selectedIds : activeIds.concat(selectedIds);
        const res = await fetch(setRetainedConnections(idsToActivate));
        dispatch(getSyncingStatus());
        if (res)
            onSuccess();
    }, [
        allowSelection,
        close,
        connectionsAutomaticallyActivated,
        connectionsLimitMap,
        orderedConnections,
        requiredTier,
        selected,
        upgradedInLoginsUpgradeFunnel,
        userTier,
    ]), requiredTier, undefined, undefined, undefined, false, false);
    const disabled = useMemo(() => allowSelection && !Object.values(selected).filter(Boolean).length, [allowSelection, selected]);
    const ListHeaderComponent = useMemo(() => {
        const limit = connectionsLimitMap[userTier];
        let subtitle = '';
        if (limit === null) {
            subtitle = 'Your plan includes unlimited active connections.';
        }
        else if (remainingLimit && remainingLimit > 0) {
            subtitle = `Your plan allows for ${remainingLimit} more active ${formatPlural('connection', 'connections', remainingLimit)}.`;
        }
        else {
            subtitle = `Your plan allows ${limit} ${formatPlural('connection', 'connections', limit)}.`;
        }
        if (allowSelection) {
            subtitle += ' Select the connections you’d like to activate.';
            if (upgradedInLoginsUpgradeFunnel) {
                subtitle += ' Connections you don’t select will become inactive.';
            }
        }
        else {
            subtitle += ' Tap below to activate the connections shown here.';
        }
        return <ScreenInfoHeader title={strings.title} subTitle={subtitle}/>;
    }, [connectionsLimitMap, userTier, remainingLimit, allowSelection, upgradedInLoginsUpgradeFunnel]);
    const onPressItem = useCallback((item) => {
        setSelected((state) => ({ ...state, [item.id]: !state[item.id] }));
    }, []);
    const renderItem = useCallback(({ item, index }) => (<FlatListItemCard index={index} itemsLength={orderedConnections.length} style={styles.card} onPress={allowSelection ? () => onPressItem(item) : undefined}>
          <BankConnectionRow data={item} showStatus={false} radioType={allowSelection ? 'multiple' : undefined} isSelected={selected[item.id] ?? false}/>
        </FlatListItemCard>), [orderedConnections.length, styles.card, allowSelection, selected, onPressItem]);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
        <Animated.FlatList data={orderedConnections} renderItem={renderItem} ListHeaderComponent={ListHeaderComponent} onScroll={scrollHandler} keyExtractor={keyExtractor} contentContainerStyle={contentContainerStyle}/>
        <Button brand floating title={!isUnlocked(userTier, requiredTier) ? `Upgrade to ${tierThemes[requiredTier].shortName}` : strings.button} onPress={onActivate} isFetching={isFetching} disabled={disabled}/>
      </AppView>);
}, ({ colors }) => ({
    headerTintColor: colors.elements.primary,
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    card: {
        backgroundColor: colors.cards.onDark,
    },
}));
export default ActivateConnectionsScreen;
